import React, { useEffect, lazy, Suspense, useState, useMemo, useRef } from 'react'; // Import lazy, Suspense, and useRef
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Toaster, toast } from 'react-hot-toast'; // Add global toast notifications
import { AnimatePresence } from 'framer-motion'; // Import AnimatePresence for spinner transitions
import { PlusCircleIcon, CheckCircleIcon, XCircleIcon, AdjustmentsHorizontalIcon, FlagIcon, TrophyIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { setSentryUser, addSentryBreadcrumb } from './config/sentry';
import { isInRecoveryMode } from './utils/recoveryModeDetection';
import { openCrispWithFeedback, FeedbackType } from './utils/crispHelpers';

// Layouts
import DashboardLayout from './components/layouts/DashboardLayout';

// Config & Hooks
// Import necessary items from routeConfig and the new hook
import { routeConfig, ROLES, ROLE_HIERARCHY } from './config/routeConfig';
import { useUserRoles } from './hooks/useUserRoles';
import { useRoutePreloader } from './hooks/useRoutePreloader'; // Import preloader hook
import { useLayoutAnimation } from './utils/layoutAnimationManager'; // Import animation manager
import { toastDeduplicationService } from './services/toastDeduplicationService'; // Import toast deduplication service

// Store & API
import { useAuthStore } from './store/authStore';
import { useUiStore } from './store/uiStore'; // Import the new UI store
import { FeatureGate } from './components/FeatureGate'; // Import FeatureGate component
import { FeatureGateRoute } from './components/FeatureGateRoute'; // NEW: Import FeatureGateRoute for route migration
import { useDashboardRenderStore } from './store/dashboardRenderStore'; // Import dashboard render store
import { useGlobalQueryStore } from './store/globalQueryStore'; // Import global query store
import { useMasterLoading } from './hooks/useMasterLoading'; // Import master loading hook
import { fetchUserSettings, UserSettingsResponse, setGetAccessTokenSilentlyRef } from './services/api';
import { unifiedPositionCache } from './services/unifiedPositionCache'; // Import unified cache

// SECURITY: Import cache security management for user transitions
import { CacheSecurityManager } from './utils/cacheSecurityManager';
import { useUserContext } from './hooks/useUserContext';
import { UserAwareCacheKeys } from './utils/userAwareCacheKeys';
import { initializeCacheSecurity, clearCacheSecurity, restoreCacheForUser } from './main';

// SECURITY: Import position store utilities for user context management
import { usePositionStore, clearAllPositionStoreKeys, clearLegacyPositionStoreKey, setPositionStoreUserId } from './store/positionStore';
import { usePositionStoreUserContext } from './hooks/usePositionStoreUserContext';
import { setNotificationStoreUserId } from './store/notificationStore';

// Real-time updates hooks
import { useIncrementalUpdates } from './hooks/useIncrementalUpdates';

// Omnisend Integration
import { OmnisendIntegration } from './components/OmnisendIntegration';
import { useUnifiedNotifications } from './hooks/useUnifiedNotifications';
import { useConditionalGlobalActivityTracking } from './hooks/useUserActivityTracking';

// Crisp Chat Integration
import CrispChat from './components/support/CrispChat';

// GDPR Cookie Consent
import { CookieConsentBanner } from './components/consent/CookieConsentBanner';
import { consentManager } from './services/consentManager';

// PWA Status Bar Theming
import { initializeStatusBarTheming, applySafeAreaInsets } from './utils/statusBarTheming';
import { getSymbolLogoUrlSync } from './utils/symbolUtils';
import { createEnhancedToastMessage, createMilestoneToastMessage, createMobileMilestoneMessage, useIsMobile } from './utils/strategyFormatting';

// CSS
import './App.css';

// --- Import AuthInitializer ---
import AuthInitializer from './components/AuthInitializer';
import ContentLoadingFallback from './components/common/ContentLoadingFallback'; // Import the new component
import BlendingSpinner from './components/common/BlendingSpinner'; // Still needed for initial app loading

// --- Import Simple Pages Directly (No Lazy Loading) ---
import Settings from './pages/Settings'; // Settings is lightweight, load directly
import FAQPage from './pages/FAQPage'; // FAQ is lightweight, load directly

// --- Lazy Load Heavy/Complex Pages Only ---
const HomePage = lazy(() => import('./pages/HomePage').then(module => ({ default: withLoadingClear(module.default) })));
const Login = lazy(() => import('./pages/Login').then(module => ({ default: withLoadingClear(module.default) })));
const PricingPlans = lazy(() => import('./pages/PricingPlans').then(module => ({ default: withLoadingClear(module.default) })));
const PublicFAQPage = lazy(() => import('./pages/PublicFAQPage').then(module => ({ default: withLoadingClear(module.default) })));
const TrialSetup = lazy(() => import('./pages/TrialSetup').then(module => ({ default: withLoadingClear(module.default) })));
const UniversalSetupOnboarding = lazy(() => import('./pages/UniversalSetupOnboarding').then(module => ({ default: withLoadingClear(module.default) })));
const Dashboard = lazy(() => import('./pages/Dashboard')); // Dashboard handles its own loading
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess').then(module => ({ default: withLoadingClear(module.default) })));
const RealTimeDiagnostic = lazy(() => import('./pages/RealTimeDiagnostic/RealTimeDiagnostic').then(module => ({ default: withLoadingClear(module.default) })));
const NotFound = lazy(() => import('./pages/NotFound').then(module => ({ default: withLoadingClear(module.default) })));
const Checkout = lazy(() => import('./pages/Checkout').then(module => ({ default: withLoadingClear(module.default) })));
const LiveTrades = lazy(() => import('./pages/LiveTrades').then(module => ({ default: withLoadingClear(module.default) })));
const ClosedTradesPage = lazy(() => import('./pages/ClosedTradesPage').then(module => ({ default: withLoadingClear(module.default) })));
// Adjust BotMaintenancePage import if it's default export now
const BotMaintenancePage = lazy(() => import('./pages/BotMaintenancePage').then(module => ({ default: withLoadingClear(module.default) })));
const LiveTradeChartView = lazy(() => import('./pages/LiveTradeChartView').then(module => ({ default: withLoadingClear(module.default) })));
const ClosedTradeChartView = lazy(() => import('./pages/ClosedTradeChartView').then(module => ({ default: withLoadingClear(module.default) }))); // Add this line
const PortfolioForecastPage = lazy(() => import('./pages/PortfolioForecastPage').then(module => ({ default: withLoadingClear(module.default) })));
const StrategyDetailsPage = lazy(() => import('./pages/StrategyDetailsPage').then(module => ({ default: withLoadingClear(module.default) })));
const PortfolioCommandCenterPage = lazy(() => import('./pages/PortfolioCommandCenterPage').then(module => ({ default: withLoadingClear(module.default) })));
const RedesignedPortfolioCommandCenter = lazy(() => import('./pages/RedesignedPortfolioCommandCenter').then(module => ({ default: withLoadingClear(module.default) }))); // Import redesigned version
const TeaserPage = lazy(() => import('./pages/TeaserPage').then(module => ({ default: withLoadingClear(module.default) }))); // Import TeaserPage
const ManageSubscriptionPage = lazy(() => import('./pages/ManageSubscriptionPage').then(module => ({ default: withLoadingClear(module.default) }))); // Add new page
const PortfolioOptimizationPage = lazy(() => import('./pages/PortfolioOptimizationPage').then(module => ({ default: withLoadingClear(module.default) }))); // Added PortfolioOptimizationPage
const AdminPage = lazy(() => import('./pages/AdminPage').then(module => ({ default: withLoadingClear(module.default) })));
const EmailPreferencesCenter = lazy(() => import('./components/EmailPreferencesCenter').then(module => ({ default: withLoadingClear(module.default) }))); // Add AdminPage
const UserLifecycleTesting = lazy(() => import('./pages/UserLifecycleTesting').then(module => ({ default: withLoadingClear(module.default) }))); // Add UserLifecycleTesting
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(module => ({ default: withLoadingClear(module.default) }))); // Add PrivacyPage (authenticated)
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(module => ({ default: withLoadingClear(module.default) }))); // Add PrivacyPolicyPage (public)
const SingleStrategyDetailsPage = lazy(() => import('./pages/SingleStrategyDetailsPage').then(module => ({ default: withLoadingClear(module.default) }))); // Add SingleStrategyDetailsPage

// ✅ FIX: Define Portfolio Command wrapper outside render cycle to prevent remounting
const PortfolioCommandWithFeatureGate = () => (
  <FeatureGate 
    featureKey="portfolio_command_center" 
    showTeaser={true}
    loadingComponent={<ContentLoadingFallback message="Checking feature access..." />}
    queryKeysToInvalidate={[
      'priority-actions-optimized',
      'portfolio-category-config',
    ]}
  >
    <Suspense fallback={null}>
      <RedesignedPortfolioCommandCenter />
    </Suspense>
  </FeatureGate>
);

// --- ProtectedRoute Component (Enhanced Version) ---
interface ProtectedRouteProps {
  children: React.ReactNode;
  minimumRequiredRole?: string; // Changed from requiredRoles
  featureName?: string;     // Feature name for the teaser page
}

const ProtectedRoute = ({ children, minimumRequiredRole, featureName }: ProtectedRouteProps) => {
  // ✅ ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // This is a React requirement - hooks must be called in the same order every render
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const location = useLocation();
  const navigationState = location.state as { fromSubscriptionSuccess?: boolean } | null;
  const { roles: userRoles, isLoading: isRolesLoading, hasEverLoaded: hasRolesEverLoaded } = useUserRoles();
  const { isApiAuthReady } = useAuthStore(state => ({ 
    isApiAuthReady: state.isApiAuthReady 
  }));
  const queryClient = useQueryClient();
  const { hasCompletedInitialLoad, setInitialLoadComplete } = useUiStore();

  // Check if an admin is currently impersonating another user
  const isAdminImpersonating = localStorage.getItem('USER_IS_ADMIN_IMPERSONATING_KEY') === 'true';

  // Special logic for Dashboard route - allow bypass of roles loading when we have cached data
  const isDashboardRoute = location.pathname === '/dashboard';

  // Simple function to check if we have basic dashboard cache  
  const hasBasicDashboardCache = useMemo(() => {
    if (!isDashboardRoute) return false;
    
    try {
      const { userTimezone } = useAuthStore.getState();
      const effectiveTimezone = userTimezone || 'UTC';
      
      const pnlTimeframes = queryClient.getQueryData(['pnlTimeframes', effectiveTimezone]);
      const accountSummary = queryClient.getQueryData(['accountSummary']);
      const strategySummary = queryClient.getQueryData(['strategySummary']);
      
      return Boolean(
        pnlTimeframes ||
        (accountSummary && Array.isArray(accountSummary) && accountSummary.length > 0) ||
        (strategySummary && Array.isArray(strategySummary) && strategySummary.length > 0)
      );
    } catch (error) {
      return false;
    }
  }, [isDashboardRoute, queryClient]);

  // Access check logic - ALL HOOKS BEFORE THIS POINT
  const accessCheckResult = useMemo(() => {
    // Auth still loading - wait for it to complete
    if (isAuthLoading || !isAuthenticated) {
      return { status: 'loading' as const, hasAccess: null };
    }
    
    // No role required - allow access
    if (!minimumRequiredRole) {
      return { status: 'granted' as const, hasAccess: true };
    }
    
    // ✅ FLICKER FIX: If we have roles data (cached or fresh), use it immediately
    // Only show loading if we have NO roles data AND it's still loading
    const hasRolesData = userRoles.length > 0;
    
    // Still loading roles AND no cached data - don't make access decision yet
    if (!hasRolesEverLoaded && isRolesLoading && !hasRolesData) {
      return { status: 'loading' as const, hasAccess: null };
    }
    
    // Check role access (using cached or fresh data)
    const requiredLevel = ROLE_HIERARCHY.indexOf(minimumRequiredRole);
    if (requiredLevel === -1) {
      return { status: 'denied' as const, hasAccess: false }; // Invalid role = deny
    }
    
    const hasAccess = userRoles.some(userRole => {
      const userLevel = ROLE_HIERARCHY.indexOf(userRole);
      return userLevel !== -1 && userLevel >= requiredLevel;
    });
    
    return { 
      status: hasAccess ? 'granted' as const : 'denied' as const, 
      hasAccess 
    };
  }, [minimumRequiredRole, userRoles, hasRolesEverLoaded, isRolesLoading, isAuthenticated, isAuthLoading]);
  
  // Modified loading check: for Dashboard with cached data, only wait for auth, not roles
  const isLoading = isDashboardRoute && hasBasicDashboardCache && isAuthenticated && isApiAuthReady
    ? false // Dashboard with cache + auth ready = no loading needed (let roles load in background)
    : isAuthLoading || (isAuthenticated && !isApiAuthReady) || isRolesLoading; // Normal loading for other routes

  // Log when we bypass roles loading for dashboard with cached data
  if (isDashboardRoute && hasBasicDashboardCache && isAuthenticated && isRolesLoading) {
    console.log('[ProtectedRoute] 🚀 Bypassing roles loading for Dashboard with cached data');
  }

  // Effect to mark initial load complete early for cached dashboard data
  useEffect(() => {
    if (isDashboardRoute && hasBasicDashboardCache && !hasCompletedInitialLoad) {
      setInitialLoadComplete();
    }
  }, [isDashboardRoute, hasBasicDashboardCache, hasCompletedInitialLoad, setInitialLoadComplete]);

  // ✅ NOW SAFE TO RETURN - ALL HOOKS HAVE BEEN CALLED
  
  // EARLY RETURN - Handle unauthenticated users FIRST before access checks
  // This prevents "Checking Access..." from showing indefinitely for unauthenticated users
  if (!isAuthenticated && !isAuthLoading) {
    const currentGlobalStatus = useUiStore.getState().globalLoadingStatus;
    if (currentGlobalStatus.isActive && currentGlobalStatus.source === 'App-InitialLoad') {
      useUiStore.getState().setGlobalLoading(false);
    }
    // Preserve query parameters (e.g., ?plan=standard&code=COMEBACK20) when redirecting to login
    const fromPath = location.pathname + location.search;
    return <Navigate to="/login" state={{ from: fromPath }} replace />;
  }

  // EARLY RETURN - Show teaser page if access denied
  if (accessCheckResult.status === 'denied') {
    console.log(`[ProtectedRoute] Access denied for ${location.pathname}. User roles [${userRoles.join(', ')}] do not meet minimum required role "${minimumRequiredRole}". Showing Teaser Page.`);
    return (
      <Suspense fallback={<RouteLoadingFallback message="Loading Upgrade Information..." />}>
        <TeaserPage
          featureName={featureName || 'This Feature'}
          minimumRequiredRole={minimumRequiredRole}
        />
      </Suspense>
    );
  }
  
  // Show loading state while determining access
  if (accessCheckResult.status === 'loading') {
    return <RouteLoadingFallback message="Checking Access..." />;
  }

  if (isLoading) {
    const isDashboardLayout = React.isValidElement(children) && 
      (children.type as any)?.name === 'DashboardLayout';
    
    if (isDashboardLayout) {
      return children; // For DashboardLayout, return it to maintain navbar/sidebar
    } else {
      return children; // For individual page components, return children to let Suspense/skeleton handle loading
    }
  }

  // Special handling for the /admin route when an admin is impersonating
  if (location.pathname.startsWith('/admin') && isAdminImpersonating) {
    console.log('[ProtectedRoute] Admin is impersonating. Allowing access to /admin route.');
    return children;
  }

  // Final access check for authenticated users
  const allowedPathsWithoutSubscription = ['/settings', '/checkout', '/subscription-success', '/faq'];
  const isPathAllowedWithoutSubscription = allowedPathsWithoutSubscription.some(allowedPath =>
    location.pathname.startsWith(allowedPath)
  );
  const justSubscribedAndNavigatingToDashboard =
    location.pathname === '/dashboard' && navigationState?.fromSubscriptionSuccess;

  // Use hasRolesEverLoaded for stable access checking - prevents premature access denials
  const shouldCheckAccess = isDashboardRoute && hasBasicDashboardCache 
    ? hasRolesEverLoaded // For cached dashboard, check access only after roles have been loaded at least once
    : true; // For other routes, we already waited for roles in isLoading above

  if (shouldCheckAccess) {
    // Check for base access role (including trial)
    const baseAccessRolesList = [ROLES.TRIAL, ROLES.BASIC, ROLES.STANDARD, ROLES.ELITE];
    const hasBaseAccessRole = baseAccessRolesList.some(role => userRoles.includes(role));

    // Redirect to pricing if no recognized access role
    if (!hasBaseAccessRole && !isPathAllowedWithoutSubscription && !justSubscribedAndNavigatingToDashboard) {
      console.log(`[ProtectedRoute] User authenticated but no recognized access role for path ${location.pathname}. Redirecting to /pricing.`);
      return <Navigate to="/pricing" replace />;
    }
  }

  // Access granted
  return children;
};

// --- Loading Component for Suspense (Moved outside App component) ---
export const ROUTE_LOADING_FALLBACK_MESSAGE = "Loading Page..."; // Define and export a constant message

const RouteLoadingFallback = ({ message = ROUTE_LOADING_FALLBACK_MESSAGE }: { message?: string }) => {
  const { hasCompletedInitialLoad } = useUiStore();
  
  // For route transitions after initial load, use content-only loading
  // This prevents the white flash by not covering the entire layout
  if (hasCompletedInitialLoad) {
    return <ContentLoadingFallback message={message} />;
  }
  
  // Only show full-screen loading on initial app load
  return null; // Let BlendingSpinner handle initial loading
};

// --- AuthenticatedUserRedirect Component ---
// Prevents homepage flash by redirecting authenticated users directly to dashboard
// But only during login flow, not when users intentionally navigate to homepage
const AuthenticatedUserRedirect: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();
  
  // Don't redirect while auth is still loading
  if (isLoading) {
    return null;
  }
  
  // Only redirect authenticated users from homepage if:
  // 1. They're on the homepage AND
  // 2. It's part of the initial login flow (detected by checking if this is their first visit)
  if (isAuthenticated && location.pathname === '/') {
    // Check if this looks like a post-login scenario
    const isPostLogin = 
      // URL contains Auth0 callback parameters
      location.search.includes('code=') || 
      location.search.includes('state=') ||
      // Or we have a flag in sessionStorage indicating recent login
      sessionStorage.getItem('auth0_post_login') === 'true';

    if (isPostLogin) {
      // Check if Auth0ProviderWithNavigate has handled routing or is handling it
      const hasSmartRoutingIntent = sessionStorage.getItem('smart_routing_active') === 'true';
      
      if (hasSmartRoutingIntent) {
        console.log('[AuthenticatedUserRedirect] Smart routing active - allowing Auth0 routing logic to proceed');
        // Don't clear flags here - let them be cleared after navigation completes
        return null;
      }
      
      // Also check if user is currently navigating away from homepage (navigation in progress)
      if (location.pathname !== '/') {
        console.log('[AuthenticatedUserRedirect] User already navigating away from homepage - allowing it');
        return null;
      }
      
      console.log('[AuthenticatedUserRedirect] Post-login detected - redirecting to dashboard');
      // Clear the post-login flag
      sessionStorage.removeItem('auth0_post_login');
      return <Navigate to="/dashboard" replace />;
    } else {
      console.log('[AuthenticatedUserRedirect] User intentionally navigated to homepage - allowing it');
    }
  }
  
  return null;
};

// --- Feedback Redirect Components ---
// Open Crisp chat with pre-filled feedback message and redirect
const FeedbackRedirect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = (params.get('type') || 'general') as FeedbackType;
    
    openCrispWithFeedback(type);
    
    // Redirect to dashboard after opening Crisp
    setTimeout(() => navigate('/dashboard'), 500);
  }, [location.search, navigate]);
  
  return <div className="loading-screen">Opening support chat...</div>;
};

const CancellationFeedbackRedirect: React.FC = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    openCrispWithFeedback('cancellation');
    setTimeout(() => navigate('/dashboard'), 500);
  }, [navigate]);
  
  return <div className="loading-screen">Opening support chat...</div>;
};

// Helper function to check if a route requires dashboard data loading
const isDashboardDataRoute = (pathname: string): boolean => {
  const dashboardDataRoutes = [
    '/dashboard',
    '/live-trades',
    '/closed-trades', 
    '/strategy-details',
    '/forecast',
    '/portfolio-command',
    '/portfolio-command-v2',
    '/portfolio-optimization'
  ];
  
  return dashboardDataRoutes.some(route => pathname.startsWith(route));
};

// Higher-order component to automatically clear loading state when a page finishes mounting
const withLoadingClear = <P extends object>(WrappedComponent: React.ComponentType<P>): React.ComponentType<P> => {
  const ComponentWithLoadingClear = (props: P) => {
    // Removed global loading manipulation to prevent layout flashing
    // Individual pages should handle their own skeleton loading states
    return <WrappedComponent {...props} />;
  };
  
  ComponentWithLoadingClear.displayName = `withLoadingClear(${WrappedComponent.displayName || WrappedComponent.name})`;
  return ComponentWithLoadingClear;
};

function App() {
  const { isAuthenticated, getAccessTokenSilently, error: auth0Error, isLoading: isAuth0Loading, logout, user: auth0User } = useAuth0();
  const queryClient = useQueryClient();
  const location = useLocation(); // Get current location for route optimization
  const navigate = useNavigate(); // Add navigate hook for clickable toasts
  const [impersonationBannerVisible, setImpersonationBannerVisible] = useState(false);
  const [impersonatedUserDisplay, setImpersonatedUserDisplay] = useState<string | null>(null);
  
  // Hook for mobile detection to enhance toast formatting
  const isMobile = useIsMobile();

  // Add consent state
  const [hasConsent, setHasConsent] = useState(consentManager.hasConsent());

  // SECURITY: Get user context for cache security
  const { userId, isReady: isUserContextReady } = useUserContext();
  
  // Get user roles for feature flag invalidation on role changes
  const { roles: userRoles, isLoading: isRolesLoading, hasEverLoaded: hasRolesEverLoaded } = useUserRoles();
  
  // 🔒 CRITICAL SECURITY: Set store user contexts IMMEDIATELY on mount
  // This ensures that when Zustand persist middleware rehydrates from localStorage,
  // it will validate against the correct user context
  useEffect(() => {
    // Set user context in both position and notification stores as soon as we have auth0 user ID
    if (auth0User?.sub) {
      setPositionStoreUserId(auth0User.sub);
      setNotificationStoreUserId(auth0User.sub);
      console.log(`🔐 [App Init] Set position & notification store user context early: ${auth0User.sub.substring(0, 20)}...`);
    }
  }, [auth0User?.sub]);
  
  // SECURITY: Initialize cache security manager (debug logging disabled for cleaner console)
  const [cacheSecurityManager] = useState(() => new CacheSecurityManager(queryClient, {
    strictMode: true,
    logViolations: true,
    enableDebugLogging: false // Only log actual security issues, not expected cleanup
  }));
  
  // SECURITY: Track previous user ID for user transitions
  const previousUserIdRef = useRef<string | null>(null);

  // Initialize PWA status bar theming
  useEffect(() => {
    console.log('[App] Initializing PWA status bar theming');
    initializeStatusBarTheming();
    applySafeAreaInsets();
  }, []);

  // Dashboard render store for controlling query execution
  const { reset: resetDashboardRender } = useDashboardRenderStore();

  // Global query store for controlling query execution
  const { setQueryingAllowed, endAuthTransition, isQueryingAllowed } = useGlobalQueryStore();
  
  // 🚀 UNIFIED CACHE: Initialize unified position cache manager
  useEffect(() => {
    console.log('[App] Initializing unified position cache manager');
    unifiedPositionCache.initialize(queryClient);
  }, [queryClient]);

  // SECURITY: Handle user transitions (login, logout, user switching)
  useEffect(() => {
    const handleUserTransition = async () => {
      if (!isUserContextReady) {
        return; // Wait for user context to be ready
      }

      const previousUserId = previousUserIdRef.current;
      
      if (userId) {
        // User logged in or switched
        console.log(`🔐 [App] User transition detected: ${previousUserId ? 'switch' : 'login'} → ${userId.substring(0, 20)}...`);
        
        // Handle login/switch scenario
        cacheSecurityManager.handleUserSwitch(previousUserId, userId);
        
        // 🚨 CRITICAL SECURITY FIX: Proactively clear ALL dangerous legacy cache keys
        console.log('🧹 [Security] Clearing ALL legacy cache keys to prevent cross-user contamination');
        
        // SECURITY: Clear legacy position store data
        clearLegacyPositionStoreKey();
        
        // SECURITY: Set user context in position store and clear previous user data
        usePositionStore.getState().setUserContext(userId);
        
        // SECURITY: Set user context in notification store and clear previous user data
        setNotificationStoreUserId(userId);
        // Import notification store dynamically to avoid circular deps
        const { useNotificationStore } = await import('./store/notificationStore');
        useNotificationStore.getState().clearAllNotifications();
        console.log('🔔 [Security] Cleared notifications for user switch');
        
        // Core trading data legacy keys
        queryClient.removeQueries({ queryKey: ['liveTrades'] });
        queryClient.removeQueries({ queryKey: ['liveTradesWidget'] });
        queryClient.removeQueries({ queryKey: ['closedTrades'] });
        
        // Dashboard legacy keys
        queryClient.removeQueries({ queryKey: ['accountSummary'] });
        queryClient.removeQueries({ queryKey: ['strategySummary'] });
        queryClient.removeQueries({ queryKey: ['pnlTimeframes'] });
        queryClient.removeQueries({ queryKey: ['accountDaily'] });
        queryClient.removeQueries({ queryKey: ['strategyDaily'] });
        queryClient.removeQueries({ queryKey: ['monthlyCalendar'] });
        queryClient.removeQueries({ queryKey: ['accountBalances'] });
        queryClient.removeQueries({ queryKey: ['accountBalancesDaily'] });
        
        // Trade filter legacy keys
        queryClient.removeQueries({ queryKey: ['tradeSymbols'] });
        queryClient.removeQueries({ queryKey: ['tradeAccounts'] });
        queryClient.removeQueries({ queryKey: ['tradeStrategies'] });
        
        // Bot management keys (user-aware)
        if (userId) {
          queryClient.removeQueries({ queryKey: UserAwareCacheKeys.strategies(userId) });
          queryClient.removeQueries({ queryKey: UserAwareCacheKeys.botRules(userId) });
          queryClient.removeQueries({ queryKey: UserAwareCacheKeys.linkedAccounts(userId) });
          queryClient.removeQueries({ queryKey: UserAwareCacheKeys.userSettings(userId) });
        }
        // Legacy keys (for backwards compatibility during migration)
        queryClient.removeQueries({ queryKey: ['strategies'] });
        queryClient.removeQueries({ queryKey: ['botRules'] });
        queryClient.removeQueries({ queryKey: ['allBotRules'] });
        queryClient.removeQueries({ queryKey: ['linkedAccounts'] });
        queryClient.removeQueries({ queryKey: ['uncoveredTradesSummary'] });
        queryClient.removeQueries({ queryKey: ['paginatedUncoveredTrades'] });
        queryClient.removeQueries({ queryKey: ['conflictsSummary'] });
        
        // Settings legacy keys
        queryClient.removeQueries({ queryKey: ['userSettings'] });
        queryClient.removeQueries({ queryKey: ['subscription'] });
        queryClient.removeQueries({ queryKey: ['notifications'] });
        
        console.log('✅ [Security] All legacy cache keys cleared');
        
        // Set user context in unified position cache
        unifiedPositionCache.setUserContext(userId);
        
        // SECURITY: Initialize secure cache persistence for this user
        initializeCacheSecurity(userId, cacheSecurityManager);
        
        // SECURITY: Restore user's cache from secure storage
        restoreCacheForUser();
        
        // Update previous user ID reference
        previousUserIdRef.current = userId;
        
        console.log('✅ [App] User login/switch handled successfully');
      } else if (previousUserId) {
        // User logged out
        console.log('🚪 [App] User logout detected');
        
        // Handle logout scenario
        cacheSecurityManager.handleUserLogout();
        
        // 🚨 CRITICAL SECURITY FIX: Also clear ALL legacy cache keys on logout
        console.log('🧹 [Security] Clearing ALL legacy cache keys on logout');
        queryClient.removeQueries({ predicate: () => true }); // Clear everything for maximum security
        
        // 🚨 CRITICAL SECURITY FIX: Clear position store and all localStorage position data
        console.log('🧹 [Security] Clearing position store and localStorage position data');
        usePositionStore.getState().clearUserData();
        clearAllPositionStoreKeys(); // Remove all position-store-persist-* keys
        
        // SECURITY: Clear cache security context
        clearCacheSecurity();
        
        // Clear previous user ID reference
        previousUserIdRef.current = null;
        
        console.log('✅ [App] User logout handled successfully');
      }
    };

    handleUserTransition().catch((error) => {
      console.error('❌ [App] Error handling user transition:', error);
      // Fallback: Clear all cache if security handling fails
      queryClient.clear();
    });
  }, [userId, isUserContextReady, cacheSecurityManager, queryClient]);
  
  // 🔄 FEATURE FLAGS: Invalidate feature flags when user roles change (e.g., after plan upgrade/downgrade)
  const previousRolesRef = useRef<string[]>([]);
  useEffect(() => {
    if (!isAuthenticated || isRolesLoading || !hasRolesEverLoaded) {
      return;
    }

    const previousRoles = previousRolesRef.current;
    const rolesChanged = previousRoles.length > 0 && 
      (previousRoles.length !== userRoles.length || 
       !previousRoles.every((role, index) => role === userRoles[index]));

    if (rolesChanged) {
      console.log('🔄 [App] User roles changed - invalidating feature flags', {
        previous: previousRoles,
        current: userRoles
      });
      
      // Invalidate and refetch feature flags when roles change
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
    }

    previousRolesRef.current = [...userRoles];
  }, [userRoles, isAuthenticated, isRolesLoading, hasRolesEverLoaded, queryClient]);
  
  // 🍞 ENHANCED TOAST: Initialize enhanced toast service with loading awareness
  useEffect(() => {
    console.log('[App] Initializing enhanced toast service');
    import('./services/enhancedToastService').then(({ enhancedToastService }) => {
      enhancedToastService.initialize();
    }).catch(console.error);
  }, []);
  
  // NEW: Master loading hook for simplified loading state management
  const { shouldShowBlendingSpinner, progressToStage, resetForLogout, masterLoadingState } = useMasterLoading();

  // Get UI store state for global loading and initial load tracking
  const { globalLoadingStatus, hasCompletedInitialLoad, setInitialLoadComplete, cacheRestorationComplete, setGlobalLoading } = useUiStore(state => ({
    globalLoadingStatus: state.globalLoadingStatus,
    hasCompletedInitialLoad: state.hasCompletedInitialLoad,
    setInitialLoadComplete: state.setInitialLoadComplete,
    cacheRestorationComplete: state.cacheRestorationComplete,
    setGlobalLoading: state.setGlobalLoading,
  }));

  // Preload common routes for better navigation performance
  useRoutePreloader();
  
  // Get animation manager for layout components
  const { reset: resetLayoutAnimations } = useLayoutAnimation();

  // Access Store: Get necessary state and setters
  const {
    accessToken,
    setAccessToken,
    setUserTimezone,
    setUserBillingName,
    isApiAuthReady,
    setLogoutFunction,
    isSetupComplete,
    setIsSetupComplete
  } = useAuthStore(state => ({
    accessToken: state.accessToken, // Ensure accessToken is selected from the store
    setAccessToken: state.setAccessToken,
    setUserTimezone: state.setUserTimezone,
    setUserBillingName: state.setUserBillingName,
    isApiAuthReady: state.isApiAuthReady,
    setLogoutFunction: state.setLogoutFunction,
    isSetupComplete: state.isSetupComplete,
    setIsSetupComplete: state.setIsSetupComplete,
  }));

  // Initialize global user activity tracking for adaptive polling - only when authenticated
  useConditionalGlobalActivityTracking(isAuthenticated && !isAuth0Loading && isApiAuthReady);

  // Get setCacheRestorationComplete function from store
  const { setCacheRestorationComplete } = useUiStore(state => ({
    setCacheRestorationComplete: state.setCacheRestorationComplete
  }));

  // FIX: Set cacheRestorationComplete only after auth stabilizes to prevent premature loading transitions
  useEffect(() => {
    if (isAuthenticated && isApiAuthReady && !cacheRestorationComplete) {
      console.log('[App] Auth stabilized - marking cache restoration complete');
      setCacheRestorationComplete();
    }
  }, [isAuthenticated, isApiAuthReady, cacheRestorationComplete, setCacheRestorationComplete]);

  // Initialize services based on consent and load consent from backend
  useEffect(() => {
    if (isAuthenticated && isApiAuthReady) {
      // Load consent preferences from backend for authenticated users
      consentManager.loadConsentFromBackend();
    }
    
    if (hasConsent) {
      consentManager.initializeServices();
    }
    
    // Listen for consent changes
    const unsubscribe = consentManager.onConsentChange((preferences) => {
      console.log('[App] Consent preferences updated:', preferences);
      setHasConsent(true);
      
      // Re-initialize services when consent changes
      consentManager.initializeServices();
    });
    
    return unsubscribe;
  }, [hasConsent, isAuthenticated, isApiAuthReady]);

  // FIX: Memoize expensive cache quality checks to prevent repeated calculations
  const hasMeaningfulCachedData = useMemo(() => {
    if (location.pathname !== '/dashboard') return false;
    
    try {
      const { userTimezone } = useAuthStore.getState();
      const effectiveTimezone = userTimezone || 'UTC';
      
      const pnlTimeframes = queryClient.getQueryData(['pnlTimeframes', effectiveTimezone]);
      const accountSummary = queryClient.getQueryData(['accountSummary']);
      const accountDaily = queryClient.getQueryData(['accountDaily', effectiveTimezone]);
      const strategySummary = queryClient.getQueryData(['strategySummary']);
      
      // Check for meaningful cache data, not just presence
      const pnlTimeframesData = pnlTimeframes && typeof pnlTimeframes === 'object' && 
        Object.keys(pnlTimeframes).length > 0;
      const accountSummaryData = accountSummary && Array.isArray(accountSummary) && 
        accountSummary.length > 0;
      const strategySummaryData = strategySummary && Array.isArray(strategySummary) && 
        strategySummary.length > 0;
      const accountDailyData = accountDaily && Array.isArray(accountDaily) && 
        accountDaily.length > 0;
      
      return Boolean(
        pnlTimeframesData || accountSummaryData || strategySummaryData || accountDailyData
      );
    } catch (error) {
      console.warn('[App] Error checking cache quality:', error);
      return false;
    }
  }, [location.pathname, queryClient, isAuthenticated]); // Only recalculate when these change

  // Immediate effect to check for cached dashboard data on app start
  useEffect(() => {
    // If we're on the dashboard route and have cached data, mark initial load complete - but only after auth is stable
    const isDashboard = location.pathname === '/dashboard';
    if (isDashboard && !hasCompletedInitialLoad && cacheRestorationComplete) {
      // Check cache after restoration is complete, but wait for auth to stabilize
      const checkCache = () => {
        try {
          // FIX: Use the memoized hasMeaningfulCachedData from outer scope (no need to redeclare)
          
          // Check if Auth0 loading has finished (either authenticated or not)
          const isAuthStable = !isAuth0Loading;
          
          // Only mark initial load complete if user is authenticated or not on a protected route
          // FIX: Dashboard.tsx is the sole completion authority - App.tsx only monitors cache presence
          // This prevents race conditions where App.tsx marks complete, then Dashboard.tsx tries to complete again
          console.log('[App] Cache analysis:', {
            hasMeaningfulCachedData,
            isAuth0Loading,
            isAuthStable: !isAuth0Loading,
            isAuthenticated
          });
          
          if (hasMeaningfulCachedData && !isAuth0Loading) {
            console.log('[App] 🚀 Dashboard with meaningful cached data detected - Dashboard.tsx will handle completion');
            // Note: Removed setInitialLoadComplete() to prevent flicker - Dashboard owns completion
          } else if (hasMeaningfulCachedData && isAuth0Loading) {
            console.log('[App] 📊 Dashboard has cached data but auth still loading - keeping spinner active');
            setGlobalLoading(true, 'Initializing Authentication...', 'App-InitialLoad');
          } else if (hasMeaningfulCachedData && isAuthStable && !isAuthenticated && location.pathname === '/dashboard') {
            console.log('[App] 📊 Dashboard has cached data but user not authenticated - will redirect to login');
            // Don't mark initial load complete, let the redirect happen
          } else {
            // If we're on dashboard route and don't have meaningful cache, activate global loading
            if (location.pathname === '/dashboard') {
              console.log('[App] 📊 Dashboard without meaningful cache detected - activating loading spinner');
              setGlobalLoading(true, 'Loading Dashboard...', 'App-InitialLoad');
              
              // Fallback: clear loading after 15 seconds to prevent infinite loading
              setTimeout(() => {
                const currentStatus = useUiStore.getState().globalLoadingStatus;
                if (currentStatus.isActive && currentStatus.source === 'App-InitialLoad') {
                  console.log('[App] ⏰ Fallback timeout - clearing loading state after 15 seconds');
                  setGlobalLoading(false);
                  setInitialLoadComplete();
                }
              }, 15000);
            }
          }
        } catch (error) {
          console.warn('[App] Error checking for cached data on app start:', error);
        }
      };
      
      // Run cache check immediately since restoration is complete
      checkCache();
    }
  }, [cacheRestorationComplete, hasCompletedInitialLoad, location.pathname, isAuth0Loading]); // Fixed: Removed setGlobalLoading to prevent infinite loop

  // Effect to check for impersonation status from localStorage for banner visibility
  useEffect(() => {
    const originalAdminToken = localStorage.getItem('td_originalAdminToken');
    const impersonatedUserDetailsString = localStorage.getItem('td_impersonatedUserDetails');
    const activeImpersonationToken = localStorage.getItem('td_impersonationToken'); // Also check for the active token

    if (originalAdminToken && impersonatedUserDetailsString && activeImpersonationToken) {
      try {
        const details = JSON.parse(impersonatedUserDetailsString);
        setImpersonatedUserDisplay(details.display || 'User');
        setImpersonationBannerVisible(true);
      } catch (e) {
        console.error("Error parsing impersonated user details from localStorage", e);
        // Clear potentially corrupted keys
        localStorage.removeItem('td_originalAdminToken');
        localStorage.removeItem('td_impersonatedUserDetails');
        localStorage.removeItem('td_impersonationToken'); // Clear this too
        localStorage.removeItem('USER_IS_ADMIN_IMPERSONATING_KEY');
        setImpersonationBannerVisible(false);
      }
    } else {
      setImpersonationBannerVisible(false);
      setImpersonatedUserDisplay(null);
    }
  }, [accessToken]); // Re-check when accessToken changes

  // Fetch user settings when API is ready
  // We fetch on initial load (isSetupComplete === null) OR periodically refetch if setup is incomplete
  const shouldFetchUserSettings = !!isApiAuthReady && isAuthenticated && 
    (isSetupComplete === null || isSetupComplete === false);
    
  const { data: userSettingsData, status: userSettingsStatus, error: userSettingsError } = useQuery<UserSettingsResponse, Error>({
    queryKey: userId ? UserAwareCacheKeys.userSettings(userId) : ['disabled'], // SECURITY: User-aware cache key
    queryFn: fetchUserSettings,
    enabled: shouldFetchUserSettings && isUserContextReady && !!userId, // Add user context validation
    staleTime: isSetupComplete === false ? 2 * 60 * 1000 : 10 * 60 * 1000, // Refetch more often if setup incomplete
    gcTime: 30 * 60 * 1000, // Garbage collect after 30 minutes instead of Infinity
    retry: 1,
  });

  // Handle side effects for user settings query
  useEffect(() => {
    // Only process user settings if the user is authenticated
    if (!isAuthenticated) {
      console.log("[App UserSettings Effect] User not authenticated, skipping user settings processing");
      return;
    }

    if (userSettingsStatus === 'success' && userSettingsData) {
      // Update loading message to indicate user settings are loaded
      if (globalLoadingStatus.isActive && globalLoadingStatus.source === 'App-InitialLoad') {
        setGlobalLoading(true, 'Setting up user preferences...', 'App-InitialLoad');
      }
      
      if (userSettingsData.timezone) {
        console.log("[App UserSettings Effect] Success. Setting timezone:", userSettingsData.timezone);
        setUserTimezone(userSettingsData.timezone);
      } else {
        console.warn("[App UserSettings Effect] Success, but no timezone found in data:", userSettingsData);
      }
      
      // Set billing name in store for Navbar components to use
      if (userSettingsData.billing_name) {
        setUserBillingName(userSettingsData.billing_name);
      }
      
      console.log("[App UserSettings Effect] Setting setup complete status from user settings:", userSettingsData.is_setup_complete);
      setIsSetupComplete(userSettingsData.is_setup_complete);

      // Set Sentry user context
      setSentryUser({
        id: userSettingsData.user_id,
        email: userSettingsData.billing_email || undefined
      });

      addSentryBreadcrumb('User settings loaded successfully', 'auth', 'info');
    } else if (userSettingsStatus === 'error') {
      console.error("[App UserSettings Effect] Error fetching user settings:", userSettingsError);
      addSentryBreadcrumb('Failed to load user settings', 'auth', 'error');
      // setIsSetupComplete(false); // Example: Assume incomplete on error
    }
  }, [isAuthenticated, userSettingsStatus, userSettingsData, userSettingsError, setUserTimezone, setUserBillingName, setIsSetupComplete]);

  // Effect to mark initial load complete when auth/setup is done
  useEffect(() => {
    // For dashboard route, we need special handling
    const isDashboard = location.pathname === '/dashboard';
    
    if (isDashboard) {
      // For dashboard, only mark complete if we have meaningful cache OR if auth is complete and we'll handle loading via spinner
      // Don't auto-complete for dashboard - let the cache check or data loading handle it
      return;
    }
    
    // For non-dashboard routes, use the original logic
    // Mark initial load as complete when:
    // 1. Auth0 is not loading AND
    // 2. Either user is not authenticated (public pages) OR user is authenticated and setup is complete
    const isInitializationComplete = !isAuth0Loading && 
      (!isAuthenticated || (isAuthenticated && isApiAuthReady && isSetupComplete !== null));
    
    if (isInitializationComplete && !hasCompletedInitialLoad) {
      console.log('[App] Marking initial load as complete for non-dashboard route');
      // Clear any App-InitialLoad loading state when completing initialization for non-dashboard routes
      const currentGlobalStatus = useUiStore.getState().globalLoadingStatus;
      if (currentGlobalStatus.isActive && currentGlobalStatus.source === 'App-InitialLoad') {
        console.log('[App] Clearing App-InitialLoad loading for non-dashboard route completion');
        setGlobalLoading(false);
      }
      // Use a small delay to ensure the UI has had time to render before marking complete
      setTimeout(() => {
        setInitialLoadComplete();
      }, 100);
    }
  }, [isAuth0Loading, isAuthenticated, isApiAuthReady, isSetupComplete, hasCompletedInitialLoad, setInitialLoadComplete, location.pathname]); // Fixed: Removed setGlobalLoading to prevent infinite loop

  // Effect to clear global loading when navigating to non-dashboard routes
  useEffect(() => {
    const isDashboardRoute = location.pathname === '/dashboard';
    const currentGlobalStatus = useUiStore.getState().globalLoadingStatus;
    
    // Clear global loading when navigating away from dashboard to other routes
    if (!isDashboardRoute && currentGlobalStatus.isActive && 
        currentGlobalStatus.source === 'App-InitialLoad' && 
        hasCompletedInitialLoad) {
      console.log('[App] Clearing global loading for navigation to non-dashboard route:', location.pathname);
      setGlobalLoading(false);
    }
  }, [location.pathname, hasCompletedInitialLoad, setGlobalLoading]);

  // Effect for handling Auth0 token, impersonation restoration, and logout function
  useEffect(() => {
    setLogoutFunction(async () => {
      try {
        // First, mark session end to ensure proper session tracking
        try {
          await fetch('/api/session/end', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('✅ Session end marked before logout');
        } catch (sessionError) {
          console.warn('⚠️ Failed to mark session end before logout:', sessionError);
          // Continue with logout even if session end fails
        }
        
        console.log('[App] Logout initiated - resetting all query state');
        resetDashboardRender(); // Reset dashboard queries on logout
        resetLayoutAnimations(); // Reset layout animations for fresh login experience
        resetForLogout('App-Logout'); // Reset master loading state for fresh login experience
        logout({ logoutParams: { returnTo: window.location.origin } });
      } catch (error) {
        console.error('Logout process failed:', error);
        // Still attempt logout even if session end fails
        logout({ logoutParams: { returnTo: window.location.origin } });
      }
    });

    const initAuth = async () => {
      const activeImpersonationToken = localStorage.getItem('td_impersonationToken');
      const originalAdminToken = localStorage.getItem('td_originalAdminToken');

      if (activeImpersonationToken && originalAdminToken) {
        console.log("[App initAuth] Impersonation active. Restoring impersonation token from localStorage.");
        setAccessToken(activeImpersonationToken);
        queryClient.clear(); // Clear React Query cache
        console.log("[App initAuth] React Query cache cleared due to active impersonation.");
        // User settings and setup completion will be fetched based on the impersonated user's token
        return;
      }

      // Proceed with normal Auth0 authentication if not impersonating
      if (isAuth0Loading) {
        console.log("[App initAuth] Auth0 is loading. Waiting...");
        return;
      }
      if (auth0Error) {
        console.error("[App initAuth] Auth0 initialization error:", auth0Error);
        setAccessToken(null);
        setIsSetupComplete(null); // Reset setup status on auth error
        queryClient.clear(); // Clear query cache on auth error
        return;
      }
      if (isAuthenticated) {
        console.log("[App initAuth] User authenticated with Auth0. Attempting to get token...");
        
        // Update loading message to indicate authentication processing
        if (globalLoadingStatus.isActive && globalLoadingStatus.source === 'App-InitialLoad') {
          setGlobalLoading(true, 'Authenticating...', 'App-InitialLoad');
        }
        
        try {
          const token = await getAccessTokenSilently();
          console.log("[App initAuth] Auth0 Token received. Calling setAccessToken in store...");
          setAccessToken(token);
          // User settings will be fetched by the useQuery hook, which depends on isApiAuthReady (set by setAccessToken)
          // and isAuthenticated. isSetupComplete will be set based on its result.
          console.log("[App initAuth] setAccessToken completed.");
          
          // Re-enable queries after successful token retrieval
          console.log("[App initAuth] Re-enabling queries after successful auth...");
          // Note: Query re-enabling is handled by separate useEffect watching isApiAuthReady
        } catch (e) {
          console.error('[App initAuth] Error getting access token from Auth0:', e);
          setAccessToken(null);
          setIsSetupComplete(null); // Reset setup status on token error
          // Note: Query re-enabling is handled by separate useEffect watching isApiAuthReady
          // queryClient.clear(); // Consider if clearing cache is desired here
        }
      } else {
        console.log("[App initAuth] User not authenticated with Auth0.");
        
        console.log("[App initAuth] Ensuring token is null and resetting setup status.");
        setAccessToken(null);
        setIsSetupComplete(null);
        queryClient.clear();
        console.log("[App initAuth] Setup status reset to null and query cache cleared.");
      }
    };

    initAuth();
    
    // Set up the getAccessTokenSilently reference for the API interceptor
    if (getAccessTokenSilently) {
      setGetAccessTokenSilentlyRef(getAccessTokenSilently);
    }
  }, [
      isAuthenticated,
      getAccessTokenSilently,
      setAccessToken,
      isAuth0Loading,
      auth0Error,
      logout, 
      setLogoutFunction,
      setIsSetupComplete,
      queryClient
    ]);

  // Effect to handle auth state stabilization for cached dashboard
  useEffect(() => {
    const isDashboard = location.pathname === '/dashboard';
    
    // When auth stabilizes, check if we can complete initial load for cached dashboard
    if (isDashboard && !hasCompletedInitialLoad && !isAuth0Loading && cacheRestorationComplete) {
      const checkCacheAfterAuth = () => {
        try {
          const { userTimezone } = useAuthStore.getState();
          const effectiveTimezone = userTimezone || 'UTC';
          
          const pnlTimeframes = queryClient.getQueryData(['pnlTimeframes', effectiveTimezone]);
          const accountSummary = queryClient.getQueryData(['accountSummary']);
          const strategySummary = queryClient.getQueryData(['strategySummary']);
          
          const hasMeaningfulCachedData = Boolean(
            (pnlTimeframes && typeof pnlTimeframes === 'object' && Object.keys(pnlTimeframes).length > 0) ||
            (accountSummary && Array.isArray(accountSummary) && accountSummary.length > 0) ||
            (strategySummary && Array.isArray(strategySummary) && strategySummary.length > 0)
          );
          
          if (hasMeaningfulCachedData) {
            console.log('[App] 🚀 Auth stabilized with meaningful cached data - completing initial load');
            setInitialLoadComplete();
          } else {
            console.log('[App] 📊 Auth stabilized but no meaningful cache - continuing with loading');
            // Let the existing loading continue if no cache
          }
        } catch (error) {
          console.warn('[App] Error checking cache after auth stabilization:', error);
        }
      };
      
      checkCacheAfterAuth();
    }
  }, [isAuth0Loading, hasCompletedInitialLoad, location.pathname, cacheRestorationComplete, setInitialLoadComplete, queryClient]);

  // Enable global queries when authentication is ready
  useEffect(() => {
    if (isApiAuthReady) {
      console.log('[App] Auth is ready, enabling global queries');
      
      // Update loading message to indicate data fetching
      if (globalLoadingStatus.isActive && globalLoadingStatus.source === 'App-InitialLoad') {
        setGlobalLoading(true, 'Loading portfolio data...', 'App-InitialLoad');
      }
      
      endAuthTransition(); // This sets isQueryingAllowed to true
    } else {
      console.log('[App] Auth not ready, keeping queries disabled');
      setQueryingAllowed(false);
    }
  }, [isApiAuthReady, endAuthTransition, setQueryingAllowed]); // Fixed: Removed globalLoadingStatus and setGlobalLoading to prevent infinite loop

  // Listen for new closed trade events from SSE
  useEffect(() => {
    const handleNewClosedTrade = (event: CustomEvent) => {
      console.log(`[App] New closed trade detected:`, event.detail);
      const { symbol, strategy, id, accountname, tradeId, pnl } = event.detail;
      
      // Check if we should show this toast (deduplication)
      const shouldShow = toastDeduplicationService.shouldShowToast({
        type: 'trade_closed',
        symbol: symbol || '',
        account: accountname || '',
        timestamp: Date.now(),
        details: {
          tradeId: tradeId || id,
          pnl: pnl
        }
      });
      
      if (!shouldShow) {
        console.log(`🔄 Skipping duplicate trade closed toast for ${symbol}/${accountname}`);
        return;
      }
      
      // Create clickable toast that navigates to the specific closed trade chart
      const handleClick = () => {
        if (id && symbol && accountname) {
          navigate(`/closed-trades/chart/${encodeURIComponent(symbol)}/${encodeURIComponent(accountname)}/${encodeURIComponent(String(id))}`, {
            state: { from: location.pathname }
          });
        } else {
          // Fallback to closed trades page with filter
          navigate(`/closed-trades?filter=${encodeURIComponent(symbol || '')}`);
        }
      };
      
      const toastContent = (
        <div className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={handleClick}>
          <img 
            src={getSymbolLogoUrlSync(symbol || '', 'small')}
            alt={symbol || 'Trade'}
            className="w-5 h-5 rounded-full flex-shrink-0"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
          <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <span className="font-medium">New Closed Trade: {symbol} ({strategy})</span>
          <span className="text-sm opacity-70 ml-auto pl-2 transition-opacity hover:opacity-100">→</span>
        </div>
      );
      
      toast(toastContent, {
        duration: 4000,
        style: {
          background: '#10b981',
          color: '#fff'
        }
      });
    };

    window.addEventListener('newClosedTrade', handleNewClosedTrade as EventListener);
    
    return () => {
      window.removeEventListener('newClosedTrade', handleNewClosedTrade as EventListener);
    };
  }, [navigate]);

  // Global position event listeners for all pages
  useEffect(() => {
    // Helper function to get symbol logo URL (updated to use local assets)
    const getSymbolLogoUrl = (symbol: string): string => {
      return getSymbolLogoUrlSync(symbol, 'small');
    };

    // Helper function to create toast content with symbol logo and icon
    const createToastContent = (symbol: string, IconComponent: React.ComponentType<any>, message: string, iconColor = "text-gray-400", pnlValue?: number, onClick?: () => void, hasColoredBackground = false) => {
      // Function to colorize PnL values in the message with high contrast support
      const colorizeMessage = (msg: string, pnl?: number, isOnColoredBackground = false) => {
        if (pnl === undefined) return msg;
        
        // Find PnL pattern in message (+$123.45 or -$123.45)
        const pnlPattern = /([+-]?\$[\d,]+\.?\d*)/g;
        const parts = msg.split(pnlPattern);
        
        return parts.map((part, index) => {
          if (pnlPattern.test(part)) {
            const isPositive = !part.startsWith('-') && (part.startsWith('+') || parseFloat(part.replace('$', '').replace(',', '')) > 0);
            
            // Use high contrast colors on colored backgrounds
            let colorClass;
            if (isOnColoredBackground) {
              colorClass = isPositive ? 'text-yellow-200 font-bold drop-shadow-sm' : 'text-orange-200 font-bold drop-shadow-sm';
            } else {
              colorClass = isPositive ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold';
            }
            
            return (
              <span key={index} className={colorClass}>
                {part}
              </span>
            );
          }
          return part;
        });
      };

      const content = (
        <div className={`flex items-center space-x-2 ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`} onClick={onClick}>
          <img 
            src={getSymbolLogoUrl(symbol)} 
            alt={symbol}
            className="w-5 h-5 rounded-full flex-shrink-0"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
          <IconComponent className={`w-4 h-4 flex-shrink-0 ${iconColor} ${hasColoredBackground ? 'drop-shadow-sm' : ''}`} />
          <span className={`font-medium ${hasColoredBackground ? 'drop-shadow-sm' : ''}`}>{colorizeMessage(message, pnlValue, hasColoredBackground)}</span>
          {onClick && <span className={`text-sm opacity-70 ml-auto pl-2 transition-opacity hover:opacity-100 ${hasColoredBackground ? 'drop-shadow-sm' : ''}`}>→</span>}
        </div>
      );

      return content;
    };

    const handlePositionPnlChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('📊 Position PnL change:', customEvent.detail);
      
      // ❌ PnL change toasts disabled by user request
      // These toasts were showing for every price movement and became too noisy
      // The PnL changes are still tracked and visible in the live trades table
      console.log('🔕 PnL change toast disabled - PnL changes are visible in live trades table');
      
      // Note: PnL changes are still processed by:
      // - Position store (live updates in UI tables)
      // - Position monitoring (for milestone detection)
      // - Cache updates (for data consistency)
      // Only the toast notifications have been disabled
    };    const handlePositionOpened = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('🟢 New position opened:', customEvent.detail);
      const { symbol, account, side, strategy, source } = customEvent.detail;
      
      // Check if this is from recovery vs real-time
      const isRecovery = source === 'session_gap_recovery' || isInRecoveryMode();
      
      import('./services/enhancedToastService').then(({ enhancedToastService }) => {
        const toastEvent = {
          type: 'position_opened' as any,
          symbol: symbol || '',
          account: account || '',
          timestamp: Date.now(),
          details: { side }
        };
        
        // Enhanced message with recovery context
        const prefix = isRecovery ? 'While away: ' : '';
        const message = createEnhancedToastMessage(
          symbol,
          `${prefix}New ${symbol} position opened`,
          strategy,
          isMobile
        );
        
        const handleClick = () => {
          if (side && account) {
            navigate(`/live-trades/chart/${encodeURIComponent(symbol)}/${encodeURIComponent(side)}/${encodeURIComponent(account)}`, {
              state: { from: location.pathname }
            });
          } else {
            navigate(`/live-trades?filter=${encodeURIComponent(symbol)}`);
          }
        };
        
        enhancedToastService.showToast(
          createToastContent(symbol, PlusCircleIcon, message, 'text-blue-600 font-semibold', undefined, handleClick),
          { duration: 4000 },
          toastEvent,
          isRecovery
        );
      }).catch(console.error);
    };

    const handlePositionClosed = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('🔴 Position closed:', customEvent.detail);
      const { symbol, account, finalPnl, strategy, source, tradeId } = customEvent.detail;
      
      // Check if this is from recovery vs real-time
      const isRecovery = source === 'session_gap_recovery' || source === 'position_snapshot_removal' || isInRecoveryMode();
      
      import('./services/enhancedToastService').then(({ enhancedToastService }) => {
        const toastEvent = {
          type: 'position_closed' as any,
          symbol: symbol || '',
          account: account || '',
          timestamp: Date.now(),
          details: { pnl: finalPnl }
        };
        
        const IconComponent = finalPnl > 0 ? CheckCircleIcon : XCircleIcon;
        const iconColor = finalPnl > 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold';
        
        // Enhanced message with recovery context
        const prefix = isRecovery ? 'While away: ' : '';
        const pnlText = `${finalPnl > 0 ? '+' : ''}$${finalPnl?.toFixed(2) || 'N/A'}`;
        const message = createEnhancedToastMessage(
          symbol,
          `${prefix}position closed ${pnlText}`,
          strategy,
          isMobile
        );
        
        const handleClick = () => {
          // Enhanced navigation with tradeId support
          if (tradeId && symbol && account) {
            // Navigate to specific closed trade chart
            navigate(`/closed-trades/chart/${encodeURIComponent(symbol)}/${encodeURIComponent(account)}/${tradeId}`, {
              state: { 
                from: location.pathname,
                context: 'toast-notification'
              }
            });
          } else {
            // Fallback to filtered closed trades
            const params = new URLSearchParams();
            if (symbol) params.set('filter', symbol);
            if (account) params.set('account', account);
            navigate(`/closed-trades?${params.toString()}`, {
              state: { 
                from: location.pathname,
                context: 'toast-notification'
              }
            });
          }
        };
        
        enhancedToastService.showToast(
          createToastContent(symbol, IconComponent, message, iconColor, finalPnl, handleClick),
          { duration: 4000 },
          toastEvent,
          isRecovery
        );
      }).catch(console.error);
    };

    const handlePositionModified = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('🔄 Position modified:', customEvent.detail);
      const { symbol, account, side, qtyChange, strategy } = customEvent.detail;
      if (qtyChange && Math.abs(qtyChange) > 0) {
        // Import enhanced toast service to prevent spam and handle loading
        import('./services/enhancedToastService').then(({ enhancedToastService }) => {
          const toastEvent = {
            type: 'position_modified' as any,
            symbol: symbol || '',
            account: account || '',
            timestamp: Date.now(),
            details: { qtyChange, side }
          };
          
          const iconColor = qtyChange > 0 ? 'text-emerald-500' : 'text-orange-500';
          const message = createEnhancedToastMessage(
            symbol,
            `position size ${qtyChange > 0 ? 'increased' : 'decreased'}`,
            strategy,
            isMobile
          );
          
          const handleClick = () => {
            if (side && account) {
              // Navigate to the specific live trade chart
              navigate(`/live-trades/chart/${encodeURIComponent(symbol)}/${encodeURIComponent(side)}/${encodeURIComponent(account)}`, {
                state: { from: location.pathname }
              });
            } else {
              // Fallback to filtered live trades page
              navigate(`/live-trades?filter=${encodeURIComponent(symbol)}`);
            }
          };
          
          enhancedToastService.showToast(
            createToastContent(symbol, AdjustmentsHorizontalIcon, message, iconColor, undefined, handleClick),
            { duration: 3000 },
            toastEvent,
            false // position modified events are typically real-time
          );
        }).catch(console.error);
      }
    };

    const handlePositionMilestone = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('🎯 Position milestone:', customEvent.detail);
      
      const { symbol, account, side, milestone_percentage, currentPnl, milestone_trend, strategy, tradeId } = customEvent.detail;
      
      // Debug log to verify milestone_trend is available
      console.log(`🎯 Milestone trend for ${symbol}: ${milestone_trend || 'undefined'}`);
      
      // ✅ SIMPLIFIED: No deduplication check needed here - backend handles all deduplication
      // Backend has 30-minute cooldown with enhanced anti-spam protection
      // If this event was dispatched, it means it passed backend validation
      
      // Use the same logic as notification dropdown for consistency
      let IconComponent;
      let iconColor;
      let directionIcon;
      let milestoneText;
        
        // Determine direction icon and text based on milestone_trend
        if (milestone_trend === 'declining') {
          directionIcon = '📉';
        } else {
          directionIcon = '📈';
        }
        
        // Generate milestone description matching notification dropdown logic
        if (milestone_percentage === 0) {
          IconComponent = FlagIcon;
          iconColor = 'text-white font-bold'; // White for high contrast on indigo background
          milestoneText = `Break Even`;
        } else if (milestone_percentage > 0) {
          IconComponent = TrophyIcon;
          iconColor = 'text-white font-bold'; // White for high contrast on green background
          milestoneText = `${Math.abs(milestone_percentage)}% Profit`;
        } else {
          IconComponent = ExclamationTriangleIcon;
          iconColor = 'text-white font-bold'; // White for high contrast on red background
          milestoneText = `${Math.abs(milestone_percentage)}% Loss`;
        }
        
        // Enhanced message with account, strategy information and direction indicator
        const pnlText = currentPnl ? ` (${currentPnl > 0 ? '+' : ''}$${currentPnl.toFixed(2)})` : '';
        const baseMessage = `${directionIcon} ${milestoneText}${pnlText}`;
        
        // Enhanced mobile-first message structure
        const message = isMobile 
          ? createMobileMilestoneMessage(symbol, directionIcon, milestoneText, currentPnl, strategy)
          : createMilestoneToastMessage(symbol, baseMessage, account, strategy, isMobile);
        
        const handleClick = () => {
          // Detect if this is a milestone for a closed position
          const isClosedPosition = Boolean(tradeId);
          
          if (isClosedPosition && tradeId) {
            // Navigate to closed trade chart
            navigate(`/closed-trades/chart/${encodeURIComponent(symbol)}/${encodeURIComponent(account)}/${tradeId}`, {
              state: { 
                from: location.pathname,
                context: 'milestone-toast'
              }
            });
          } else if (side && account) {
            // Navigate to live trade chart
            navigate(`/live-trades/chart/${encodeURIComponent(symbol)}/${encodeURIComponent(side)}/${encodeURIComponent(account)}`, {
              state: { 
                from: location.pathname,
                context: 'milestone-toast'
              }
            });
          } else {
            // Fallback based on position status
            const basePath = isClosedPosition ? '/closed-trades' : '/live-trades';
            navigate(`${basePath}?filter=${encodeURIComponent(symbol)}`, {
              state: { 
                from: location.pathname,
                context: 'milestone-toast'
              }
            });
          }
        };
        
        // Enhanced mobile-aware toast styling
        const toastStyle = {
          background: milestone_percentage === 0 ? '#6366f1' : (milestone_percentage > 0 ? '#10b981' : '#ef4444'),
          color: '#fff',
          fontWeight: '600',
          fontSize: isMobile ? '13px' : '14px', // Slightly smaller on mobile
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
          lineHeight: isMobile ? '1.3' : '1.4',   // Better mobile line spacing
          padding: isMobile ? '12px 16px' : '16px 20px', // Responsive padding
          minHeight: isMobile ? '60px' : 'auto',   // Ensure touch target
        };

        toast(createToastContent(symbol, IconComponent, message, iconColor, currentPnl, handleClick, true), {
          duration: 5000,  // Longer duration for milestones
          style: toastStyle
        });
    };

    const handleTradeClosedToast = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('💰 Trade closed toast:', customEvent.detail);
      const { symbol, account, tradeId, finalPnl, strategy, source } = customEvent.detail;
      
      const isRecovery = source === 'session_gap_recovery' || isInRecoveryMode();
      
      import('./services/enhancedToastService').then(({ enhancedToastService }) => {
        const toastEvent = {
          type: 'trade_closed' as any,
          symbol: symbol || '',
          account: account || '',
          timestamp: Date.now(),
          details: { pnl: finalPnl, tradeId }
        };
        
        const IconComponent = finalPnl > 0 ? CheckCircleIcon : XCircleIcon;
        const iconColor = finalPnl > 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold';
        
        const prefix = isRecovery ? 'While away: ' : '';
        const pnlText = `${finalPnl > 0 ? '+' : ''}$${finalPnl?.toFixed(2) || 'N/A'}`;
        const message = createEnhancedToastMessage(
          symbol,
          `${prefix}trade closed ${pnlText}`,
          strategy,
          isMobile
        );
        
        const handleClick = () => {
          if (tradeId && symbol && account) {
            navigate(`/closed-trades/chart/${encodeURIComponent(symbol)}/${encodeURIComponent(account)}/${tradeId}`, {
              state: { 
                from: location.pathname,
                context: 'trade-closed-toast'
              }
            });
          } else {
            const params = new URLSearchParams();
            if (symbol) params.set('filter', symbol);
            if (account) params.set('account', account);
            navigate(`/closed-trades?${params.toString()}`, {
              state: { 
                from: location.pathname,
                context: 'trade-closed-toast'
              }
            });
          }
        };
        
        enhancedToastService.showToast(
          createToastContent(symbol, IconComponent, message, iconColor, finalPnl, handleClick),
          { duration: 4000 },
          toastEvent,
          isRecovery
        );
      }).catch(console.error);
    };

    // Add event listeners
    window.addEventListener('openPositionPnlChange', handlePositionPnlChange);
    window.addEventListener('openPositionOpened', handlePositionOpened);
    window.addEventListener('openPositionClosed', handlePositionClosed);
    window.addEventListener('openPositionModified', handlePositionModified);
    window.addEventListener('openPositionMilestone', handlePositionMilestone);
    window.addEventListener('tradeClosed', handleTradeClosedToast);
    
    return () => {
      // Cleanup event listeners
      window.removeEventListener('openPositionPnlChange', handlePositionPnlChange);
      window.removeEventListener('openPositionOpened', handlePositionOpened);
      window.removeEventListener('openPositionClosed', handlePositionClosed);
      window.removeEventListener('openPositionModified', handlePositionModified);
      window.removeEventListener('openPositionMilestone', handlePositionMilestone);
      window.removeEventListener('tradeClosed', handleTradeClosedToast);
    };
  }, [navigate]);
  
  // ✅ UNIFIED NOTIFICATION SYSTEM: Single source of truth for all real-time updates
  // Replaces legacy SSE, session gap detection, and smart polling
  const { isInitialized: unifiedNotificationsReady } = useUnifiedNotifications();

  // ✅ INCREMENTAL CACHE UPDATES: Listen for metrics_calculated events and process with hybrid cache manager
  // This is the sole processor for incremental cache updates using frontend math operations
  useIncrementalUpdates({
    enabled: isApiAuthReady && isQueryingAllowed && unifiedNotificationsReady && isUserContextReady, // Only enabled when unified system is ready
    userId: userId || undefined, // SECURITY: Pass authenticated user ID for user-aware cache operations
  });

  // 🔐 SECURITY: Position store user context management for data isolation
  usePositionStoreUserContext({
    enabled: true,
    autoSwitchUser: true,
    validateOnLoad: true
  });

  // Helper function to check if error is user cancellation (same as in Login.tsx)
  const isUserCancellation = (error: any): boolean => {
    if (!error) return false;
    
    // Check error message for common Auth0 cancellation patterns
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      return (
        message.includes('popup closed') ||
        message.includes('user closed') ||
        message.includes('cancelled') ||
        message.includes('canceled') ||
        message.includes('popup_closed_by_user') ||
        message.includes('authorization_cancelled') ||
        message.includes('window closed') ||
        message.includes('user_cancelled') ||
        message.includes('login_required') ||
        message.includes('consent_required') ||
        message.includes('interaction_required')
      );
    }
    
    // Check Auth0 specific error properties
    if (error && typeof error === 'object') {
      const errorCode = error.error || error.code;
      const errorDescription = error.error_description || error.description;
      
      // Common Auth0 cancellation error codes
      if (errorCode === 'access_denied' || 
          errorCode === 'user_cancelled' ||
          errorCode === 'popup_closed_by_user' ||
          errorCode === 'cancelled') {
        return true;
      }
      
      // Check error description
      if (errorDescription && typeof errorDescription === 'string') {
        const desc = errorDescription.toLowerCase();
        return desc.includes('cancelled') || desc.includes('closed') || desc.includes('denied');
      }
    }
    
    return false;
  };

  // Auth0 error display - but ignore user cancellations
  if (auth0Error && !isUserCancellation(auth0Error)) {
    const handleErrorLogout = async () => {
      try {
        // First, mark session end to ensure proper session tracking
        try {
          await fetch('/api/session/end', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('✅ Session end marked before error logout');
        } catch (sessionError) {
          console.warn('⚠️ Failed to mark session end before error logout:', sessionError);
          // Continue with logout even if session end fails
        }
        
        const authState = useAuthStore.getState();
        if (authState.logoutAuth0) {
          authState.logoutAuth0();
        } else {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      } catch (error) {
        console.error('Error logout process failed:', error);
        // Still attempt logout even if session end fails
        const authState = useAuthStore.getState();
        if (authState.logoutAuth0) {
          authState.logoutAuth0();
        } else {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      }
    };

    return (
      <div className="error-screen">
        <h1>Authentication Error</h1>
        <p>{auth0Error.message}</p>
        <p>Please try logging in again or contact support.</p>
        <button onClick={handleErrorLogout}>Logout</button>
      </div>
    );
  }

  // console.log("App rendering. Auth status:", { isAuthenticated, isAuth0Loading, error: auth0Error?.message, isApiAuthReady, isSetupComplete });

  // --- Global Loading for Auth0 Initialization is now handled by AuthInitializer ---
  // const isRestoringImpersonation = !!localStorage.getItem('td_impersonationToken') && !!localStorage.getItem('td_originalAdminToken');
  // if (isAuth0Loading && !isRestoringImpersonation && !auth0Error) { // Added !auth0Error here
  //   console.log("[App] Auth0 is initializing and not restoring an impersonation session. Displaying global loading screen.");
  //   return <div className="loading-screen">Initializing Authentication...</div>;
  // }


  const handleStopImpersonationGlobal = () => {
    const originalAdminToken = localStorage.getItem('td_originalAdminToken');
    if (originalAdminToken) {
      setAccessToken(originalAdminToken); // Revert to admin's original token
      localStorage.removeItem('td_originalAdminToken');
      localStorage.removeItem('td_impersonatedUserDetails');
      localStorage.removeItem('td_impersonationToken'); // Clear the impersonation token
      localStorage.removeItem('USER_IS_ADMIN_IMPERSONATING_KEY'); // Clear impersonation flag
      
      queryClient.clear(); // Clear React Query cache to remove impersonated user's data

      setImpersonationBannerVisible(false);
      setImpersonatedUserDisplay(null);
      // Force a reload to ensure all components re-evaluate with the admin token
      window.location.href = '/admin'; 
    } else {
      console.warn("Original admin token not found, cannot stop impersonation globally.");
      // Potentially force logout or redirect to login if state is inconsistent
      const authState = useAuthStore.getState();
      if (authState.logoutAuth0) {
        authState.logoutAuth0();
      } else {
        logout({ logoutParams: { returnTo: window.location.origin } });
      }
    }
  };

  // Effect to monitor dashboard data availability and clear loading when ready
  useEffect(() => {
    // Only check for dashboard route and when global loading is active
    if (location.pathname !== '/dashboard' || !globalLoadingStatus.isActive || globalLoadingStatus.source !== 'App-InitialLoad') {
      return;
    }

    try {
      const { isApiAuthReady } = useAuthStore.getState();
      
      // FIX: Use memoized cache check instead of recalculating
      const hasLoadedData = hasMeaningfulCachedData;
      
      // Only mark initial load complete when BOTH auth is stable AND we have meaningful data
      // Auth is stable when Auth0 is not loading AND API auth is ready
      const isAuthStable = !isAuth0Loading && isApiAuthReady;
      const shouldCompleteInitialLoad = hasLoadedData && isAuthStable;
      
      if (shouldCompleteInitialLoad) {
        console.log('[App] 🎯 Dashboard data loaded and auth stable - marking initial load complete', {
          hasLoadedData,
          isAuthStable,
          isAuth0Loading,
          isApiAuthReady,
          isAuthenticated
        });
        setGlobalLoading(false);
        setInitialLoadComplete();
      } else if (hasLoadedData && !isAuthStable) {
        console.log('[App] 📊 Dashboard data available but auth still transitioning - keeping spinner active', {
          hasLoadedData,
          isAuthStable,
          isAuth0Loading,
          isApiAuthReady,
          isAuthenticated
        });
      }
    } catch (error) {
      console.warn('[App] Error checking dashboard data availability:', error);
    }
  }, [location.pathname, queryClient, setInitialLoadComplete, isAuth0Loading, isAuthenticated]); // Fixed: Removed globalLoadingStatus and setGlobalLoading to prevent infinite loop

  // NEW: Master loading stage management
  useEffect(() => {
    console.log('[App] Master loading useEffect triggered:', {
      isAuth0Loading,
      isAuthenticated,
      cacheRestorationComplete,
      isApiAuthReady,
      pathname: location.pathname,
      currentStage: masterLoadingState.stage,
      currentMessage: masterLoadingState.message,
      source: masterLoadingState.source
    });

    // CRITICAL: If already complete, don't re-trigger any stage changes
    // This prevents debounced stage changes from firing after completion
    if (masterLoadingState.stage === 'complete' && !masterLoadingState.isActive) {
      console.log('[App] Loading already complete - skipping all stage management');
      return;
    }

    // CRITICAL FIX: If Dashboard is already managing the loading state, don't interfere
    // This prevents competing effects between App.tsx and Dashboard.tsx
    const isDashboardManaging = masterLoadingState.source?.includes('Dashboard');
    const isDashboardRoute = isDashboardDataRoute(location.pathname);
    const isAlreadyInDashboardStage = masterLoadingState.stage === 'initial-dashboard' || masterLoadingState.stage === 'dashboard';
    
    if (isDashboardManaging && isDashboardRoute && isAlreadyInDashboardStage) {
      console.log('[App] Dashboard already managing loading state - skipping App intervention', {
        source: masterLoadingState.source,
        stage: masterLoadingState.stage
      });
      return;
    }

    // Handle logout completion: if we're in logout mode but user is no longer authenticated
    // and Auth0 is not loading, complete the logout process
    if (masterLoadingState.stage === 'logout') {
      if (!isAuth0Loading && !isAuthenticated) {
        console.log('[App] Logout complete - user not authenticated, completing loading');
        progressToStage('complete', '', 'App-LogoutComplete');
        return;
      } else {
        console.log('[App] Logout in progress - waiting for auth state to clear');
        return;
      }
    }

    const isPostLogin = 
      // URL contains Auth0 callback parameters
      location.search.includes('code=') || 
      location.search.includes('state=') ||
      // Or we have a flag in sessionStorage indicating recent login
      sessionStorage.getItem('auth0_post_login') === 'true' ||
      // Or navigation state indicates login flow
      (location.state as any)?.fromLogin === true;

    if (isAuth0Loading && !isAuthenticated) {
      // User is not authenticated and Auth0 is loading - show auth stage
      // But only if we're not already in a completed state on homepage
      if (!(location.pathname === '/' && masterLoadingState.stage === 'complete')) {
        console.log('[App] Auth0 loading and not authenticated - showing auth stage');
        progressToStage('auth', 'Authenticating...', 'App-Auth0Loading');
      } else {
        console.log('[App] Auth0 loading but already complete on homepage - skipping auth stage');
      }
    } else if (!isAuth0Loading && !isAuthenticated) {
      // User is not authenticated and Auth0 is not loading - complete loading (show homepage)
      console.log('[App] Not authenticated and Auth0 not loading - completing loading');
      progressToStage('complete', '', 'App-NotAuthenticated');
    } else if (isAuthenticated && !isAuth0Loading && cacheRestorationComplete) {
      // User is authenticated and cache is restored - check what to do based on route and context
      const isDashboardRoute = isDashboardDataRoute(location.pathname);
      
      // FIX: Check if Dashboard.tsx or Onboarding is managing loading - if so, don't interfere
      const isDashboardManaging = masterLoadingState.source?.includes('Dashboard') || 
                                  masterLoadingState.source?.includes('Onboarding');
      if (isDashboardRoute && isDashboardManaging) {
        console.log('[App] Dashboard/Onboarding managing loading - App.tsx will not interfere', {
          stage: masterLoadingState.stage,
          source: masterLoadingState.source
        });
        return; // Early exit - let Dashboard/Onboarding control everything
      }
      
      if (location.pathname === '/' && !isPostLogin) {
        // User is on homepage and it's NOT a post-login scenario
        // But check if they will be redirected - if authenticated, they might be about to redirect
        // Only complete if they've been on homepage for a bit (not just landed)
        const hasBeenOnHomepageAWhile = hasCompletedInitialLoad;
        if (hasBeenOnHomepageAWhile) {
          // User intentionally navigated back to homepage - complete loading immediately
          progressToStage('complete', '', 'App-AuthenticatedOnHomepage');
        } else {
          // User just loaded - they might be about to redirect, keep auth stage
          console.log('[App] Authenticated user on homepage (initial load) - waiting for potential redirect');
          progressToStage('auth', 'Loading...', 'App-AuthenticatedHomepageInitial');
        }
      } else if (location.pathname === '/' && isPostLogin) {
        // User is on homepage but it's a post-login scenario - keep auth stage active until redirect completes
        progressToStage('auth', 'Redirecting to dashboard...', 'App-PostLoginRedirect');
      } else if (isDashboardRoute && isPostLogin) {
        // SPLASH SCREEN: Post-login dashboard load - use initial-dashboard stage to show BlendingSpinner
        console.log('[App] Post-login dashboard route load - using initial-dashboard stage for splash screen');
        progressToStage('initial-dashboard', 'Loading dashboard...', 'App-PostLoginDashboard');
      } else if (isDashboardRoute && !isApiAuthReady) {
        // User is on dashboard route but API auth not ready yet - show auth stage
        progressToStage('auth', 'Setting up authentication...', 'App-DashboardApiAuthNotReady');
      } else if (isDashboardRoute && isApiAuthReady) {
        // User is on dashboard route and API auth ready
        if (location.pathname === '/dashboard') {
          // NAVIGATION: Normal dashboard navigation - check if already complete or at correct stage
          const isAlreadyComplete = masterLoadingState.stage === 'complete';
          
          if (isAlreadyComplete) {
            console.log('[App] Dashboard already complete - no stage change needed');
            // Do nothing - loading is complete
          } else if (isDashboardManaging) {
            console.log('[App] Dashboard/Onboarding managing loading - App.tsx will not interfere', {
              currentStage: masterLoadingState.stage,
              source: masterLoadingState.source
            });
            // CRITICAL: Let Dashboard.tsx or Onboarding handle completion without interference
            // This prevents App.tsx from re-triggering loading stages after they complete
          } else {
            // Only intervene if Dashboard hasn't started managing yet
            // Check if this is a true initial load or just navigation
            const isInitialLoad = !hasCompletedInitialLoad || 
                                 sessionStorage.getItem('auth0_post_login') === 'true' ||
                                 (location.state as any)?.fromOnboarding === true;
            
            if (isInitialLoad && masterLoadingState.stage !== 'initial-dashboard') {
              console.log('[App] Initial dashboard load - using initial-dashboard stage for splash screen');
              progressToStage('initial-dashboard', 'Loading dashboard...', 'App-InitialDashboardLoad');
            } else {
              console.log('[App] Dashboard navigation - setting dashboard stage, letting Dashboard.tsx complete when data ready');
              // FIX: Set dashboard stage instead of doing nothing - this keeps the BlendingSpinner visible
              // until Dashboard.tsx confirms data is loaded and calls completeLoading
              if (masterLoadingState.stage !== 'dashboard' && masterLoadingState.stage !== 'initial-dashboard') {
                progressToStage('dashboard', 'Loading dashboard...', 'App-DashboardNavigation');
              }
            }
          }
        } else {
          // Other dashboard routes - complete loading since they don't have integrated loading management
          console.log('[App] User on dashboard route with API auth ready - completing loading for route:', location.pathname);
          progressToStage('complete', '', 'App-DashboardRouteComplete');
        }
      } else if (!isApiAuthReady) {
        // User is on other routes but API auth not ready yet - show auth stage
        progressToStage('auth', 'Setting up authentication...', 'App-ApiAuthNotReady');
      } else {
        // User is on other routes and API auth ready - complete loading
        progressToStage('complete', '', 'App-OtherRouteReady');
      }
    }
  }, [isAuth0Loading, isAuthenticated, cacheRestorationComplete, isApiAuthReady, location.pathname, location.search, location.state, progressToStage]);

  return (
    <Sentry.ErrorBoundary 
      fallback={({ resetError }) => (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 max-w-md w-full text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
            <p className="text-gray-300 mb-6">
              We've been notified about this error. Please try refreshing the page.
            </p>
            <div className="space-y-3">
              <button
                onClick={resetError}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      )}
      showDialog
    >
      {/* Omnisend Integration - Track user activity */}
      <OmnisendIntegration />
      
      {/* Cookie Consent Banner */}
      <CookieConsentBanner 
        onConsentGiven={(preferences) => {
          setHasConsent(true);
          console.log('[App] User provided consent:', preferences);
        }}
      />
      
      <AnimatePresence>
        {shouldShowBlendingSpinner() && (
          <BlendingSpinner statusMessage={masterLoadingState.message || 'Loading...'} />
        )}
      </AnimatePresence>
      <Suspense fallback={(() => {
        // Don't show fallback if loading is complete
        if (masterLoadingState.stage === 'complete' && !masterLoadingState.isActive) {
          return null;
        }
        
        // Check if current route uses DashboardLayout (protected routes)
        const protectedRoutes = [
          '/dashboard', '/portfolio-command', '/portfolio-command-v2', '/portfolio-optimization',
          '/live-trades', '/closed-trades', '/strategy-details', '/forecast', '/bot-maintenance',
          '/settings', '/email-preferences', '/privacy', '/faq', '/manage-subscription',
          '/admin', '/real-time-diagnostic', '/lifecycle-testing'
        ];
        
        const isDashboardLayoutRoute = protectedRoutes.some(route => 
          location.pathname === route || location.pathname.startsWith(route + '/')
        );
        
        // For routes using DashboardLayout, return null to let the inner Suspense in DashboardLayout handle it
        // This keeps the Navbar and Sidebar visible during page loading
        if (isDashboardLayoutRoute) {
          console.log('[App Suspense] Dashboard layout route detected - letting inner Suspense handle loading:', location.pathname);
          return null;
        }
        
        // For other routes (public pages), show the route loading fallback
        console.log('[App Suspense] Showing fallback for route:', location.pathname);
        return <RouteLoadingFallback />;
      })()}>
        <div className="app-container">
          <AuthInitializer> {/* AuthInitializer will manage its own loading via global store */}
          {impersonationBannerVisible && (
            <div 
              style={{
                position: 'fixed', 
                bottom: 0, // Changed from top: 0
                left: 0, 
                width: '100%', 
                backgroundColor: '#FFF3CD', // Light yellow
                color: '#856404', // Dark yellow/brown text
                padding: '10px 20px', 
                textAlign: 'center',
                zIndex: 10000, // Ensure it's on top
                borderTop: '1px solid #FFEEBA' // Changed from borderBottom
              }}
            >
              Impersonating: <strong>{impersonatedUserDisplay}</strong>. All actions are performed as this user.
              <button 
                onClick={handleStopImpersonationGlobal}
                style={{
                  marginLeft: '20px',
                  padding: '5px 10px',
                  backgroundColor: '#DC3545', // Red
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Stop Impersonation
              </button>
            </div>
          )}
          
          {/* Handle authenticated user redirects outside of Routes */}
          <AuthenticatedUserRedirect />
          
          <Routes location={location}>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/pricing" element={<PricingPlans />} />
            <Route path="/help" element={<PublicFAQPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/trial-setup" element={<TrialSetup />} />
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <UniversalSetupOnboarding />
              </ProtectedRoute>
            } />

            {/* Redirect routes for email templates */}
            <Route path="/reactivate" element={
              <Navigate 
                to={`/pricing?intent=reactivate${location.search ? '&' + location.search.substring(1) : ''}`} 
                replace 
              />
            } />
            <Route path="/upgrade" element={
              <Navigate 
                to={`/pricing?intent=upgrade${location.search ? '&' + location.search.substring(1) : ''}`} 
                replace 
              />
            } />
            <Route path="/demo" element={
              <Navigate to="/help" replace />
            } />
            <Route path="/privacy" element={
              <Navigate to="/privacy-policy" replace />
            } />
            
            {/* Feedback routes - open Crisp chat with pre-filled messages */}
            <Route path="/feedback" element={<FeedbackRedirect />} />
            <Route path="/cancellation-feedback" element={<CancellationFeedbackRedirect />} />

            {/* Routes requiring authentication - DashboardLayout wrapper provides basic auth check */}
            {/* Individual routes use FeatureGateRoute for feature-specific access control */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              {/* Map over routeConfig to generate protected routes within the layout */}
              {routeConfig.map(({ path }) => {
                // Find the component associated with the path
                // 🔥 FIX: Return JSX directly instead of creating functions
                // This prevents component remounting on every parent render
                // ✅ SEAMLESS LOADING PATTERN:
                // FeatureGateRoute renders page immediately → page shows skeleton
                // Access check happens in background (cached, fast)
                // Result: Single continuous loading experience (skeleton → content OR skeleton → teaser)
                let pageElement;
                switch (path) {
                  case '/dashboard': pageElement = (
                    <FeatureGateRoute 
                      featureKey="dashboard"
                      queryKeysToInvalidate={['dashboardMetrics', 'tradesSummary']}
                    >
                      <Dashboard />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/portfolio-command': pageElement = <PortfolioCommandWithFeatureGate />; break;
                  case '/portfolio-command-v2': pageElement = <PortfolioCommandCenterPage />; break; // Keep old version as v2 for backup
                  case '/portfolio-optimization': pageElement = (
                    <FeatureGateRoute 
                      featureKey="portfolio_optimization"
                      queryKeysToInvalidate={['strategyPnlSummary', 'strategyPnlDaily', 'botRules']}
                    >
                      <PortfolioOptimizationPage />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/live-trades': pageElement = (
                    <FeatureGateRoute 
                      featureKey="live_trades"
                      queryKeysToInvalidate={['liveTrades', 'positions']}
                    >
                      <LiveTrades />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/closed-trades': pageElement = (
                    <FeatureGateRoute 
                      featureKey="closed_trades"
                      queryKeysToInvalidate={['closedTrades', 'tradeHistory']}
                    >
                      <ClosedTradesPage />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/strategy-details': pageElement = (
                    <FeatureGateRoute 
                      featureKey="strategy_details"
                      queryKeysToInvalidate={['strategyDetails', 'strategyMetrics']}
                    >
                      <StrategyDetailsPage />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/forecast': pageElement = (
                    <FeatureGateRoute 
                      featureKey="portfolio_forecast"
                      queryKeysToInvalidate={['forecastData', 'forecastMetrics']}
                    >
                      <PortfolioForecastPage />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/bot-maintenance': pageElement = (
                    <FeatureGateRoute 
                      featureKey="bot_maintenance"
                      queryKeysToInvalidate={['botRules', 'botConfig']}
                    >
                      <BotMaintenancePage />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/settings': pageElement = (
                    <FeatureGateRoute 
                      featureKey="account_settings"
                      queryKeysToInvalidate={['userSettings', 'userProfile']}
                    >
                      <Settings />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/email-preferences': pageElement = (
                    <FeatureGateRoute 
                      featureKey="email_preferences"
                      queryKeysToInvalidate={['emailPreferences']}
                    >
                      <EmailPreferencesCenter />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/privacy': pageElement = (
                    <FeatureGateRoute 
                      featureKey="privacy_settings"
                      queryKeysToInvalidate={['privacySettings', 'dataExport']}
                    >
                      <PrivacyPage />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/faq': pageElement = (
                    <FeatureGateRoute 
                      featureKey="help_faq"
                    >
                      <FAQPage />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  // Add case for the new route if it's in routeConfig
                  case '/manage-subscription': pageElement = <ManageSubscriptionPage />; break;
                  case '/admin': pageElement = (
                    <FeatureGateRoute 
                      featureKey="admin_panel"
                      showTeaser={false}
                    >
                      <AdminPage />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/real-time-diagnostic': pageElement = (
                    <FeatureGateRoute 
                      featureKey="real_time_diagnostic"
                      showTeaser={false}
                    >
                      <RealTimeDiagnostic />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  case '/lifecycle-testing': pageElement = (
                    <FeatureGateRoute 
                      featureKey="lifecycle_testing"
                      showTeaser={false}
                    >
                      <UserLifecycleTesting />
                    </FeatureGateRoute>
                  ); break; // Migrated to FeatureGateRoute
                  default: return null; // Skip routes without a component mapping here
                }

                return (
                  <Route
                    key={path}
                    path={path}
                    element={pageElement}
                  />
                );
              })}
              {/* Add routes with dynamic params separately */}
              <Route
                  path="/live-trades/chart/:symbol/:side/:accountName"
                  element={
                    <FeatureGateRoute 
                      featureKey="live_trades"
                      queryKeysToInvalidate={['liveTrades', 'positions']}
                    >
                      <LiveTradeChartView />
                    </FeatureGateRoute>
                  }
              />
            <Route
                  path="/closed-trades/chart/:symbol/:accountName/:tradeId"
                  element={
                    <FeatureGateRoute 
                      featureKey="closed_trades"
                      queryKeysToInvalidate={['closedTrades', 'tradeHistory']}
                    >
                      <Suspense fallback={<RouteLoadingFallback message="Loading Chart..." />}>
                        <ClosedTradeChartView />
                      </Suspense>
                    </FeatureGateRoute>
                  }
              />
              {/* Single Strategy Details Route */}
              <Route
                  path="/strategies/:strategyId"
                  element={
                    <FeatureGateRoute
                      featureKey="strategy_details"
                      queryKeysToInvalidate={['strategyDetails', 'strategyMetrics']}
                    >
                      <Suspense fallback={<RouteLoadingFallback message="Loading Strategy Details..." />}>
                        <SingleStrategyDetailsPage />
                      </Suspense>
                    </FeatureGateRoute>
                  }
              />
            </Route>

            {/* Standalone Protected Routes (without DashboardLayout) */}
            <Route
              path="/checkout"
              element={
                  <ProtectedRoute>
                      <Checkout />
                  </ProtectedRoute>
              }
          />
           <Route
              path="/subscription-success"
              element={
                  <ProtectedRoute>
                      <SubscriptionSuccess />
                  </ProtectedRoute>
              }
          />


          {/* Catch-all for undefined routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </AuthInitializer> {/* Close AuthInitializer */}
        </div>
      </Suspense>
      
      {/* Global toast notifications */}
      <Toaster 
        position="top-right" 
        containerClassName="mt-20"
        containerStyle={{ zIndex: 9998 }}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 6000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 8000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      
      {/* Crisp Chat Widget - Available globally when auth is ready */}
      {isApiAuthReady && <CrispChat />}
    </Sentry.ErrorBoundary>
  );
}

export default App;